import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { favoritesAPI } from '../api';
import { useAuth } from '../hooks/useAuth';

const IMG_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const getSellerBadge = (u) => {
  if (!u) return null;
  const badges = {
    top_seller: { label: 'Top Seller', color: 'bg-yellow-400 text-yellow-900', icon: '⭐' },
    trusted: { label: 'Trusted', color: 'bg-green-500 text-white', icon: '✅' },
    verified: { label: 'Verified', color: 'bg-blue-500 text-white', icon: '🔷' },
    active: { label: 'Active', color: 'bg-gray-500 text-white', icon: '🟢' },
  };
  if (u.seller_badge && badges[u.seller_badge]) return badges[u.seller_badge];
  const days = Math.floor((Date.now() - new Date(u.created_at).getTime()) / 86400000);
  if (days <= 30) return { label: 'Just Joined', color: 'bg-purple-500 text-white', icon: '👤' };
  return { label: 'Active', color: 'bg-gray-500 text-white', icon: '🟢' };
};

const getTimeAgo = (d) => {
  if (!d) return '';
  const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const AdCard = ({ item, type, isFav, onToggleFav }) => {
  const { user } = useAuth();
  const [favLoading, setFavLoading] = useState(false);
  const [imgError, setImgError] = useState(false);

  const images = item.images || [];
  const firstImg = images[0];
  const imgSrc = firstImg ? (firstImg.startsWith('http') ? firstImg : `${IMG_BASE}${firstImg}`) : null;

  let price;
  if (type === 'service') {
    if (item.price && Number(item.price) > 0) {
      const label = item.price_type === 'negotiable' ? ' (Negotiable)' : item.price_type === 'hourly' ? '/hr' : '';
      price = 'FRW ' + Number(item.price).toLocaleString() + label;
    } else {
      price = 'Contact for price';
    }
  } else {
    price = item.price ? 'FRW ' + Number(item.price).toLocaleString() : 'Contact for price';
  }

  const seller = item.user || item.seller || {};
  const badge = getSellerBadge(seller);
  const loc = [item.district_name, item.province_name].filter(Boolean).join(', ');
  const link = type === 'service' ? `/services/${item.id}` : `/products/${item.id}`;

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || favLoading) return;
    setFavLoading(true);
    try {
      const res = await favoritesAPI.toggle({ item_type: type, item_id: item.id });
      onToggleFav?.(type, item.id, res.data?.action || (isFav ? 'removed' : 'added'));
    } catch { }
    setFavLoading(false);
  };

  return (
    <Link to={link} className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
      {/* Image */}
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        {imgSrc && !imgError ? (
          <img src={imgSrc} alt={item.title || item.product_name} onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </div>
        )}

        {/* Fav button */}
        {user && (
          <button onClick={toggleFav}
            className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all backdrop-blur-sm ${isFav ? 'bg-primary-600 text-white shadow-md' : 'bg-black/25 text-white hover:bg-black/40'}`}>
            <svg className="w-4 h-4" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
          </button>
        )}

        {/* Type + Condition */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${type === 'service' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>
            {type}
          </span>
          {item.condition && item.condition !== 'N/A' && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-800/70 text-white backdrop-blur-sm">
              {item.condition}
            </span>
          )}
        </div>

        {/* Seller badge */}
        {badge && (
          <span className={`absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.color} backdrop-blur-sm shadow-sm`}>
            <span>{badge.icon}</span> {badge.label}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-primary-600 font-bold text-sm truncate">{price}</span>
          <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">{getTimeAgo(item.created_at)}</span>
        </div>
        <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {item.title || item.product_name}
        </h3>
        {loc && (
          <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1 truncate">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
            {loc}
          </p>
        )}
      </div>
    </Link>
  );
};

export default AdCard;
